/* eslint-disable camelcase */
import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  selection,
} from 'redux-resource-plugins';
import {
  actionTypes,
  updateResources,
} from '../actions';
import {
  throwError,
  getEntityType,
} from './helpers';
import {
  capitalizeFirstLetter,
} from '../utils';
import {
  getSelectedIds,
  getResourceById,
  getGroupItemAttrs,
} from '../selectors';
import api from '../api';

export default function* groupFlow(reportChannel) {
  function* getGroupEntity({
    groupId,
    resourceType,
    requestKey,
  }) {
    const actions = createActionCreators('read', {
      resourceType,
      requestKey: requestKey || 'getGroupEntity',
    });
    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        api.getEntity,
        {
          id: groupId,
          type: getEntityType(resourceType),
        },
      );

      yield eff.put(actions.succeeded({
        resources: [
          {
            id: groupId,
            clients: response.clients.map(client => client.id),
          },
        ],
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* getGroupEntityAssignments({
    status,
    groupId,
    resourceType,
  }) {
    const actions = createActionCreators('read', {
      resourceType: 'clientGroups',
      requestKey: `get${capitalizeFirstLetter(resourceType)}Assignments`,
    });

    yield eff.put(actions.pending());

    try {
      const entityAssignments = yield eff.call(
        api.getGroupAssignment,
        {
          status,
          groupId,
          type: getEntityType(resourceType),
        },
      );
      yield eff.put(actions.succeeded({
        resources: [
          {
            id: groupId,
            [resourceType]: entityAssignments.map(e => e.original_id || e.id),
          },
        ],
        includedResources: {
          [resourceType]: entityAssignments.map(e => ({ ...e, id: e.original_id || e.id })),
        },
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* moveToGroup({
    callbacks,
    currentEntityId,
  }) {
    const selectedClientIds = yield eff.select(getSelectedIds('clients'));
    const selectedGroupIds = yield eff.select(getSelectedIds('clientGroups'));

    const idPairs = (currentEntityId
      ? [currentEntityId]
      : selectedClientIds
    )
      .reduce((acc, clientId) => acc.concat(
        selectedGroupIds
          .map(groupId => ({
            clientId,
            groupId,
          })),
      ), []);

    try {
      yield eff.all(
        idPairs.map(pair => eff.call(
          api.moveToGroup,
          {
            clientId: pair.clientId,
            groupId: pair.groupId,
            body: {
              clientId: pair.clientId,
              groupId: pair.groupId,
            },
          },
        )),
      );

      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess();
    } catch (e) {
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);

      yield eff.call(throwError, e);
    }
  }

  function* deleteFromGroup({ groupId, selectedClientIds, callbacks }) {
    const actions = createActionCreators('read', {
      resourceType: 'clientGroups',
      requestKey: 'deleteGroupMembers',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      yield eff.all(
        selectedClientIds.map(id => eff.call(
          api.deleteFromGroup,
          {
            clientId: id,
            groupId,
          },
        )),
      );
      const response = yield eff.call(
        api.getEntity,
        {
          id: groupId,
          type: 'client_groups',
        },
      );
      yield eff.put(actions.succeeded({
        resources: [{
          ...response,
          clients: response.clients.map(member => member.id),
        }],
      }));

      yield eff.put(
        selection.deselectResources('clients', selectedClientIds),
      );
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess();

      const addedAttrs = yield eff.all(
        selectedClientIds.map(id => eff.select(
          getGroupItemAttrs(
            groupId,
            id,
          ),
        )),
      );
      yield eff.all(
        addedAttrs.map(
          attrs => eff.put({
            type: 'GC_ANALYTICS',
            eventKey: 'client_group_member_removed',
            attrs,
          }),
        ),
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onError) callbacks.onError(e);
      yield eff.call(throwError, e);
    }
  }

  function* addToGroup({
    clientId,
    groupId,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'clientGroups',
      requestKey: 'addMemberToGroup',
    });

    yield eff.put(actions.pending());

    try {
      yield eff.call(
        api.moveToGroup,
        {
          clientId,
          groupId,
          body: {
            clientId,
            groupId,
          },
        },
      );

      const response = yield eff.call(
        api.getEntity,
        {
          id: groupId,
          type: 'client_groups',
        },
      );

      const clients = response.clients.map(client => client.id);
      yield eff.put(actions.succeeded({
        resources: [
          {
            id: groupId,
            clients,
          },
        ],
      }));

      const attrs = yield eff.select(
        getGroupItemAttrs(
          groupId,
          clientId,
        ),
      );
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'client_group_member_added',
        attrs,
      });
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* assignGroup({
    id,
    payload,
    listName,
  }) {
    const resourceType = 'groupAssignments';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'assignGroup',
    });
    const isWorkout = payload.template_type === 'WorkoutTemplate';

    try {
      yield eff.put(actions.pending());
      const response = yield eff.call(
        api.createAssignment,
        {
          id,
          body: payload,
          type: 'client_groups',
        },
      );

      const group = yield eff.select(
        getResourceById('clientGroups', id),
      );

      const templateType = isWorkout
        ? 'workoutTemplates'
        : 'programTemplates';
      const oldData = group[templateType] || [];

      yield eff.put(
        updateResources({
          resourceType: 'clientGroups',
          resources: [{
            id,
            [templateType]: [
              ...oldData,
              +payload.template_id,
            ],
          }],
        }),
      );

      yield eff.put(actions.succeeded({
        resources: [response],
        includedResources: {
          groupAssignments: [{
            id: response.id,
          }],
        },
        customLists: {
          groupAssignments: listName,
        },
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client_group.assigned',
            params: {
              type: (isWorkout
                ? 'Workout template'
                : 'Program template'
              ),
            },
          },
        },
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'error',
            key: 'item.client_group.empty',
          },
        },
      );
      yield eff.call(throwError, e, reportChannel);
    }
  }

  function* unAssignGroup({
    id,
    payload,
    callbacks,
  }) {
    const resourceType = 'groupAssignments';
    const actions = createActionCreators('delete', {
      resourceType,
      resources: [id],
      requestKey: 'unAssignGroup',
    });
    const isWorkout = payload.template_type === 'WorkoutTemplate';
    yield eff.put(
      actions.pending(),
    );

    try {
      const response = yield eff.call(
        api.deleteAssignment,
        {
          id,
          body: payload,
          type: 'client_groups',
        },
      );

      const group = yield eff.select(
        getResourceById('clientGroups', id),
      );

      const templateType = isWorkout
        ? 'workoutTemplates'
        : 'programTemplates';
      const oldData = group[templateType] || [];

      yield eff.put(
        updateResources({
          resourceType: 'clientGroups',
          resources: [{
            id,
            [templateType]: oldData.filter(
              item => item !== +payload.template_id,
            ),
          }],
        }),
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client_group.unassigned',
            params: {
              type: (isWorkout
                ? 'Workout template'
                : 'Program template'
              ),
            },
          },
        },
      );

      if (callbacks?.onSuccess) callbacks.onSuccess();
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
    } catch (e) {
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e, reportChannel);
    }
  }

  function* archiveAssignment({ id, payload, callbacks }) {
    const actions = createActionCreators('delete', {
      resources: [id],
      resourceType: 'groupAssignments',
      requestKey: 'archiveAssigment',
    });
    yield eff.put(actions.pending());
    try {
      const result = yield eff.call(
        api.archiveAssignment,
        {
          id,
          body: payload,
          type: 'client_groups',
        },
      );
      yield eff.put(actions.succeeded({
        resources: [result],
      }));
    } catch (e) {
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e, reportChannel);
    }
  }

  function* restoreAssignment({ id, payload, callbacks }) {
    const actions = createActionCreators('delete', {
      resources: [id],
      resourceType: 'groupAssignments',
      requestKey: 'restoreAssigment',
    });
    yield eff.put(actions.pending());
    try {
      const result = yield eff.call(
        api.restoreAssignment,
        {
          id,
          body: payload,
          type: 'client_groups',
        },
      );
      if (callbacks?.onSuccess) {
        callbacks.onSuccess(result);
      }
      yield eff.put(actions.succeeded({
        resources: [result],
      }));
    } catch (e) {
      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e, reportChannel);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_ADD_TO_GROUP, addToGroup);
  yield eff.takeEvery(actionTypes.REQUEST_GROUP_ENTITY, getGroupEntity);
  yield eff.takeEvery(actionTypes.REQUEST_GROUP_ENTITY_ASSIGNMENTS, getGroupEntityAssignments);
  yield eff.takeEvery(actionTypes.REQUEST_MOVE_TO_GROUP, moveToGroup);
  yield eff.takeEvery(actionTypes.REQUEST_DELETE_FROM_GROUP, deleteFromGroup);

  yield eff.takeEvery(actionTypes.REQUEST_ASSIGN_GROUP, assignGroup);
  yield eff.takeEvery(actionTypes.REQUEST_UNASSIGN_GROUP, unAssignGroup);
  yield eff.takeEvery(actionTypes.REQUEST_ARCHIVE_ASSIGNMENT, archiveAssignment);
  yield eff.takeEvery(actionTypes.REQUEST_RESTORE_ASSIGNMENT, restoreAssignment);
}
